import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useLogout, useParametre } from '../api/hooks'
import HeroEditor from './HeroEditor'

const ANNEE_COURANTE = 2026

const tabs = [
  { to: '/', label: 'Accueil', icon: '🏖️', end: true },
  { to: '/nuitees', label: 'Nuitées', icon: '🌙' },
  { to: '/couchages', label: 'Couchages', icon: '🛏️' },
  { to: '/repas', label: 'Repas', icon: '🍽️' },
  { to: '/menus', label: 'Menus', icon: '🍲' },
  { to: '/depenses', label: 'Dépenses', icon: '🛒' },
  { to: '/familles', label: 'Familles', icon: '👨‍👩‍👧' },
  { to: '/comptes', label: 'Comptes', icon: '🧮' },
  { to: '/planning-wc', label: 'Planning wc', icon: '🚽' },
]

export default function Layout() {
  const logout = useLogout()
  const { pathname } = useLocation()
  const isHome = pathname === '/'
  const param = useParametre(ANNEE_COURANTE)
  const heroPos = param.data?.hero_position ?? 35
  const [editorOpen, setEditorOpen] = useState(false)

  return (
    <div className="min-h-full flex flex-col">
      {/* === Hero band === */}
      <header className="relative overflow-hidden group/hero">
        <div className={isHome ? 'h-56 sm:h-80' : 'h-28 sm:h-32'}>
          <picture>
            <source media="(min-width: 1024px)" srcSet="/hero-large.jpg" />
            <source media="(min-width: 640px)" srcSet="/hero-medium.jpg" />
            <img
              src="/hero-small.jpg"
              alt="Maison familiale Etchehoun à Hossegor"
              className="absolute inset-0 w-full h-full object-cover transition-[object-position] duration-300"
              style={{ objectPosition: `center ${heroPos}%` }}
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-ocean-900/30 via-ocean-900/20 to-ocean-900/70" />
        </div>

        {/* Bouton discret pour ajuster le cadrage — visible sur Accueil uniquement */}
        {isHome && (
          <button
            type="button"
            onClick={() => setEditorOpen(true)}
            className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur text-white border border-white/30 flex items-center justify-center shadow-soft transition opacity-60 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Ajuster le cadrage de l'image"
            title="Ajuster le cadrage de l'image"
          >
            <span aria-hidden className="text-base">🖼️</span>
          </button>
        )}

        {/* Titre superposé */}
        <div className="absolute top-0 inset-x-0">
          <div className="max-w-6xl mx-auto px-4 pt-4 sm:pt-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-3xl sm:text-4xl drop-shadow">🏖️</div>
              <div>
                <h1 className="text-white text-xl sm:text-2xl font-display font-bold tracking-tight drop-shadow">
                  Etchehoun · Hossegor
                </h1>
                <p className="text-ocean-100 text-xs sm:text-sm font-medium drop-shadow">
                  Séjour familial 2026 · 25 juillet → 8 août
                </p>
              </div>
            </div>
            <button
              onClick={() => logout.mutate()}
              className="text-xs sm:text-sm bg-white/15 hover:bg-white/25 backdrop-blur text-white px-3 py-1.5 rounded-lg border border-white/20 transition"
              aria-label="Déconnexion"
            >
              <span className="hidden sm:inline">Déconnexion</span>
              <span className="sm:hidden">↪</span>
            </button>
          </div>
        </div>

      </header>

      {/* === Navigation desktop / tablette === */}
      <nav className="hidden sm:block bg-white/80 backdrop-blur-md border-b border-ocean-100 sticky top-0 z-20 shadow-soft">
        <div className="max-w-6xl mx-auto px-2 flex overflow-x-auto no-scrollbar">
          {tabs.map(t => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                `px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition flex items-center gap-2 ${
                  isActive
                    ? 'border-coral-500 text-ocean-900'
                    : 'border-transparent text-ocean-600 hover:text-ocean-900 hover:bg-ocean-50/60'
                }`
              }
            >
              <span aria-hidden>{t.icon}</span>
              <span>{t.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* === Contenu === */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-3 sm:px-5 py-4 sm:py-6 pb-20 sm:pb-6 anim-in">
        <Outlet />
      </main>

      {/* === Tab bar mobile === */}
      <nav
        aria-label="Navigation principale mobile"
        className="sm:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-ocean-100 shadow-[0_-2px_10px_-2px_rgba(15,76,110,0.10)] z-30"
      >
        <div className="grid grid-cols-9">
          {tabs.map(t => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 text-[9px] gap-0.5 transition px-0.5 ${
                  isActive ? 'text-coral-600 font-bold' : 'text-ocean-600'
                }`
              }
            >
              <span className="text-base leading-none" aria-hidden>{t.icon}</span>
              <span className="leading-none">{t.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Modal d'ajustement de l'image hero */}
      {editorOpen && param.data && (
        <HeroEditor
          annee={ANNEE_COURANTE}
          current={heroPos}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </div>
  )
}
